// Time matrix: rows = days (each made up of 1+ lanes), columns = time slots.
// Built on CSS Grid with explicit placement of every grid item.
//
// Interaction model:
//  - Click on empty cell        → ADD appointment in lane 0
//  - Click on covered cell      → REMOVE that appointment
//  - Shift + click (on a cell whose candidate would conflict somewhere)
//                               → ADD overlapping appointment in next free lane
//  - Drag continues the mode set at pointerdown.
//  - In OVERLAP-ADD drag, the lane is locked to the lane of the first action.
//
// Phantom lanes:
//  When the user holds Shift and hovers over a position whose candidate would
//  need a lane that does not yet exist, we temporarily expand the day by one
//  "phantom" lane so the preview overlay can be placed correctly. The phantom
//  is purely visual — it has no data — and disappears as soon as the preview
//  no longer requires it.

import type { Store } from '../store';
import type { Appointment, FinderConfig } from '../types';
import { enumerateDates, formatDateShort, formatTime, fromISODate } from '../time';
import { holidaysForCountries, holidayFlagsForCountries } from '../holidays';
import {
  appointmentAtCell,
  candidateFromCell,
  findConflict,
  laneCountForDate,
  nextFreeLane,
  slotCount,
  slotStartMinutes,
  zoneForMinutes,
} from '../logic';
import { projectBusyEvents } from '../busy-overlay';
import type { BusyEvent } from '../ics-parser';
import { el, clear } from './dom';


const TIME_COLUMN_WIDTH = '6.5rem';
// Spaltenmindestbreite kommt aus CSS-Variable, damit Mediaqueries Mobile vs.
// Desktop unterscheiden können — Touch will ≥2rem, Maus kommt mit 1rem klar.
const MIN_CELL_WIDTH = 'var(--matrix-min-cell, 2rem)';

type DragMode = 'add' | 'remove' | 'overlap-add';

let dragActive = false;
let dragMode: DragMode = 'add';
let dragLockedLane: number | null = null;
let dragLastKey: string | null = null;
let globalListenersBound = false;
let shiftHeld = false;

let scrollerRef: HTMLElement | null = null;
let storeRef: Store | null = null;

/** Browser-only busy times from the user's pasted calendar. Never persisted. */
let busyEvents: BusyEvent[] = [];

/** Set the user-imported busy events and trigger a matrix rebuild. */
export function setBusyEvents(events: BusyEvent[]): void {
  busyEvents = events;
  rebuildMatrix();
}

/** Phantom lanes per date — value is the minimum laneCount required for visuals.
 *  Always at most one entry (we only preview one position at a time). */
let phantomLanes = new Map<string, number>();

/** The most recent pointer position over a cell, tracked for re-evaluating
 *  preview state after Shift state changes. */
let lastPointerCell: { date: string; col: number; lane: number } | null = null;
/** Most recent pointer event over a cell — used so renderPreview can place
 *  the tooltip at the current cursor position, including when Shift is
 *  pressed without the cursor moving. */
let lastPointerEvent: PointerEvent | null = null;

export function renderMatrix(store: Store): HTMLElement {
  const root = el('section', { class: 'matrix-panel', 'aria-label': 'Zeitmatrix' });

  // Touch-only: Edit-/Sperr-Toggle. Im Sperrmodus reagieren die Zellen
  // nicht auf Touch (pointer-events: none) → der Browser nutzt jeden Touch
  // als Scroll-Geste, der User legt nicht versehentlich Termine an.
  const editToggle = el('button',
    {
      type: 'button',
      class: 'matrix-toggle-edit',
      'aria-pressed': 'false',
      title: 'Matrix sperren — verhindert versehentliches Eintragen beim Scrollen',
    },
    '🔒 Zum Scrollen sperren',
  ) as HTMLButtonElement;
  editToggle.addEventListener('click', () => {
    const locked = !root.classList.contains('is-locked');
    root.classList.toggle('is-locked', locked);
    editToggle.textContent = locked ? '✏️ Wieder bearbeiten' : '🔒 Zum Scrollen sperren';
    editToggle.setAttribute('aria-pressed', String(locked));
    editToggle.title = locked
      ? 'Bearbeiten — Tippen legt wieder Termine an'
      : 'Matrix sperren — verhindert versehentliches Eintragen beim Scrollen';
  });

  const hint = el(
    'div',
    { class: 'matrix-hint' },
    el('span', { class: 'matrix-hint-line' },
      'Klick legt einen Termin an oder entfernt ihn. ',
      el('kbd', { class: 'kbd' }, 'Shift'),
      ' + Klick legt einen überlappenden Termin in einer neuen Spur darunter an. Mit gedrückter Maustaste ziehen für mehrere.',
    ),
    el('span', { class: 'matrix-hint-privacy' },
      '🔒 ',
      el('strong', {}, 'Privacy:'),
      ' Solange du nichts teilst, bleibt deine Planung lokal in diesem Browser-Tab.',
    ),
    editToggle,
  );

  const scroller = el('div', { class: 'matrix-scroller' });
  root.append(hint, scroller);

  scrollerRef = scroller;
  storeRef = store;

  if (!globalListenersBound) {
    bindGlobalListeners();
    globalListenersBound = true;
  }

  store.subscribe(() => rebuildMatrix());

  return root;
}

function bindGlobalListeners(): void {
  window.addEventListener('pointermove', (e) => {
    if (!dragActive) return;
    const cell = cellFromPoint(e.clientX, e.clientY);
    if (!cell) return;
    const key = `${cell.date}|${cell.col}|${cell.lane}`;
    if (key === dragLastKey) return;
    dragLastKey = key;
    if (storeRef) applyDragAction(storeRef, cell.date, cell.col, cell.lane);
  });
  const endDrag = () => {
    dragActive = false;
    dragLastKey = null;
    dragLockedLane = null;
  };
  window.addEventListener('pointerup', endDrag);
  window.addEventListener('pointercancel', endDrag);
  window.addEventListener('blur', endDrag);

  window.addEventListener('keydown', (e) => {
    if (e.key === 'Shift' && !shiftHeld) {
      shiftHeld = true;
      reevaluatePreview();
    }
  });
  window.addEventListener('keyup', (e) => {
    if (e.key === 'Shift') {
      shiftHeld = false;
      reevaluatePreview();
    }
  });
}

function cellFromPoint(
  x: number,
  y: number,
): { cell: HTMLElement; date: string; col: number; lane: number } | null {
  const elAt = document.elementFromPoint(x, y);
  if (!elAt) return null;
  const cell = (elAt as HTMLElement).closest('.matrix-cell') as HTMLElement | null;
  if (!cell) return null;
  const date = cell.dataset.date;
  const colStr = cell.dataset.col;
  const laneStr = cell.dataset.lane;
  if (!date || colStr === undefined || laneStr === undefined) return null;
  const col = Number(colStr);
  const lane = Number(laneStr);
  if (Number.isNaN(col) || Number.isNaN(lane)) return null;
  return { cell, date, col, lane };
}

function rebuildMatrix(): void {
  if (!scrollerRef || !storeRef) return;
  const s = storeRef.get();
  clear(scrollerRef);
  scrollerRef.append(buildMatrix(storeRef, s.config, s.appointments));
}

/** Compare two single-entry phantom maps for equality. */
function phantomMapsEqual(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) if (b.get(k) !== v) return false;
  return true;
}

function setPhantomLanes(next: Map<string, number>): void {
  if (phantomMapsEqual(phantomLanes, next)) return;
  phantomLanes = next;
  rebuildMatrix();
  // After rebuild, previous previewEl is detached. Re-render the preview using
  // the most recent pointer cell so the user sees the correct overlay.
  if (lastPointerCell) renderPreview(lastPointerCell);
}

function clearPhantomLanes(): void {
  if (phantomLanes.size === 0) return;
  phantomLanes = new Map();
  rebuildMatrix();
}

function reevaluatePreview(): void {
  if (!lastPointerCell) return;
  if (dragActive) return;
  renderPreview(lastPointerCell);
}

function buildMatrix(store: Store, config: FinderConfig, appointments: Appointment[]): HTMLElement {
  const cols = slotCount(config);
  const dates = enumerateDates(config.rangeStart, config.rangeEnd, config.allowedWeekdays);

  if (cols <= 0 || dates.length === 0) {
    return el(
      'div',
      { class: 'matrix-empty' },
      cols <= 0 ? 'Tageszeitfenster ist leer.' : 'Keine Tage im gewählten Zeitraum.',
    );
  }

  // Effective lane count per date: the bigger of real and phantom.
  const laneCounts = dates.map((d) => {
    const real = laneCountForDate(appointments, d);
    const phantom = phantomLanes.get(d) ?? 0;
    return Math.max(real, phantom);
  });
  const rowOffsets: number[] = [];
  let cursor = 2; // grid row 1 is the header
  for (let i = 0; i < dates.length; i++) {
    rowOffsets.push(cursor);
    cursor += laneCounts[i];
  }

  const grid = el('div', { class: 'matrix-grid' });
  grid.style.gridTemplateColumns = `${TIME_COLUMN_WIDTH} repeat(${cols}, minmax(${MIN_CELL_WIDTH}, 1fr))`;

  // Clear phantoms when the pointer leaves the grid entirely.
  grid.addEventListener('pointerleave', () => {
    lastPointerCell = null;
    lastPointerEvent = null;
    hideTooltip();
    hidePreview();
    clearPhantomLanes();
  });

  // Tooltip follows the cursor across cells without re-rendering the preview.
  grid.addEventListener('pointermove', (e) => {
    lastPointerEvent = e;
    if (tooltipEl && tooltipEl.style.display !== 'none') {
      positionTooltip(tooltipEl, e);
    }
  });

  const holidayMap = config.holidayCountries.length > 0
    ? holidaysForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();
  const holidayFlagsMap = config.holidayCountries.length > 0
    ? holidayFlagsForCountries(config.holidayCountries, config.rangeStart, config.rangeEnd)
    : new Map<string, string>();

  // Header row.
  // Each sticky header (corner, col-headers, row-labels) spans its grid axis
  // entirely so the element has room to "stick" inside its containing block.
  // CSS handles the visual constraint via fixed width/height.
  const corner = el('div', { class: 'matrix-corner' });
  corner.style.gridRow = '1 / -1';
  corner.style.gridColumn = '1 / -1';
  grid.append(corner);
  for (let c = 0; c < cols; c++) {
    const minutes = slotStartMinutes(config, c);
    const showLabel = config.slotSizeMinutes >= 30 || minutes % 60 === 0;
    const header = el(
      'div',
      { class: `matrix-col-header zone-${zoneForMinutes(minutes)}` },
      showLabel ? formatTime(minutes) : '',
    );
    header.style.gridRow = '1 / -1';
    header.style.gridColumn = String(c + 2);
    grid.append(header);
  }

  dates.forEach((date, dateIdx) => {
    const weekday = fromISODate(date).getDay();
    const isWeekend = weekday === 0 || weekday === 6;
    const holidayName = holidayMap.get(date);
    const laneCount = laneCounts[dateIdx];
    const dayStartRow = rowOffsets[dateIdx];
    const realLaneCount = laneCountForDate(appointments, date);

    /* The row label is rendered in two layers: an outer grid-cell wrapper
     * (full row width, transparent — provides the containing block for
     * sticky) and an inner element that's `position: sticky; left: 0` and
     * visually 6.5rem wide. This works around a Mobile-Safari quirk where
     * sticky inside a single-column grid item releases too early. */
    const rowLabelWrap = el('div', { class: 'matrix-row-label-wrap' });
    rowLabelWrap.style.gridRow = `${dayStartRow} / ${dayStartRow + laneCount}`;
    rowLabelWrap.style.gridColumn = '1 / -1';

    const rowLabel = el('div', { class: 'matrix-row-label' });
    rowLabel.append(el('span', { class: 'row-date' }, formatDateShort(date)));
    const holidayFlags = holidayFlagsMap.get(date);
    if (holidayFlags) {
      const flagSpan = el('span', { class: 'row-holiday' });
      flagSpan.append(el('span', { class: 'row-holiday-flag' }, holidayFlags));
      if (holidayName) {
        flagSpan.title = holidayName;
        flagSpan.append(el('span', { class: 'row-holiday-name' }, holidayName));
      }
      rowLabel.append(flagSpan);
    }
    if (isWeekend) rowLabel.classList.add('is-weekend');
    if (holidayName) rowLabel.classList.add('is-holiday');
    if (dateIdx > 0) rowLabel.classList.add('is-day-divider');
    rowLabelWrap.append(rowLabel);
    grid.append(rowLabelWrap);

    for (let lane = 0; lane < laneCount; lane++) {
      const isPhantom = lane >= realLaneCount;
      for (let c = 0; c < cols; c++) {
        const cell = el('div', {
          class: 'matrix-cell',
          'data-date': date,
          'data-col': c,
          'data-lane': lane,
        });
        cell.style.gridRow = String(dayStartRow + lane);
        cell.style.gridColumn = String(c + 2);
        if (isWeekend) cell.classList.add('is-weekend');
        if (holidayName) cell.classList.add('is-holiday');
        if (lane > 0) cell.classList.add('is-sublane');
        if (lane === 0 && dateIdx > 0) cell.classList.add('is-day-top');
        if (isPhantom) cell.classList.add('is-phantom');

        cell.addEventListener('pointerdown', (e) => onCellPointerDown(e, store, date, c, lane));
        cell.addEventListener('pointerenter', (e) => onCellEnter(e, date, c, lane));
        grid.append(cell);
      }
    }
  });

  // Calendar busy overlays — rendered BEFORE appointment overlays so they sit
  // visually behind. They span all lanes of their day.
  if (busyEvents.length > 0) {
    const ranges = projectBusyEvents(busyEvents, config, dates);
    for (const r of ranges) {
      const dateIdx = dates.indexOf(r.date);
      if (dateIdx < 0) continue;
      const overlay = buildBusyOverlay(r, rowOffsets[dateIdx], laneCounts[dateIdx]);
      grid.append(overlay);
    }
  }

  // Appointment overlays
  for (const appt of appointments) {
    const dateIdx = dates.indexOf(appt.date);
    if (dateIdx < 0) continue;
    const absRow = rowOffsets[dateIdx] + appt.lane;
    const overlay = buildAppointmentOverlay(config, appt, absRow);
    if (overlay) grid.append(overlay);
  }

  return grid;
}

function buildBusyOverlay(
  range: { startCol: number; endCol: number; summary: string; isRecurring: boolean },
  dayStartRow: number,
  laneCount: number,
): HTMLElement {
  const overlay = el('div', { class: 'busy-overlay' });
  overlay.style.gridRow = `${dayStartRow} / ${dayStartRow + laneCount}`;
  overlay.style.gridColumn = `${range.startCol + 2} / ${range.endCol + 2}`;
  const summaryText = range.summary || 'belegt';
  overlay.title = range.isRecurring ? `${summaryText} (wiederkehrend)` : summaryText;
  // Title text inside the block — only shown when body has `show-bg-titles`.
  overlay.append(el('span', { class: 'busy-overlay-text' }, summaryText));
  return overlay;
}

function buildAppointmentOverlay(
  config: FinderConfig,
  appt: Appointment,
  gridRow: number,
): HTMLElement | null {
  const startCol = (appt.startMinutes - config.dayStartMinutes) / config.slotSizeMinutes;
  const spanCols = (appt.endMinutes - appt.startMinutes) / config.slotSizeMinutes;
  if (!Number.isFinite(startCol) || !Number.isFinite(spanCols) || spanCols <= 0) return null;

  const overlay = el('div', {
    class: 'appointment-overlay',
    'data-appt-id': appt.id,
    'data-lane': String(appt.lane),
    /* color cycle 0..3 — keeps the visual finite even when the user stacks
     * many overlap lanes on the same day. */
    'data-color-lane': String(appt.lane % 4),
  });
  const gridColStart = Math.round(startCol) + 2;
  const gridColEnd = gridColStart + Math.round(spanCols);
  overlay.style.gridRow = String(gridRow);
  overlay.style.gridColumn = `${gridColStart} / ${gridColEnd}`;

  overlay.append(
    el('span', { class: 'appointment-overlay-label' },
      `${formatTime(appt.startMinutes)}–${formatTime(appt.endMinutes)}`),
  );
  return overlay;
}

// --- Drag handling ---

function onCellPointerDown(
  e: PointerEvent,
  store: Store,
  date: string,
  col: number,
  lane: number,
): void {
  if (e.button !== 0) return;
  e.preventDefault();

  const s = store.get();
  const cellStart = slotStartMinutes(s.config, col);
  const cellEnd = cellStart + s.config.slotSizeMinutes;
  const covering = appointmentAtCell(s.appointments, date, cellStart, cellEnd, lane);
  const wantOverlap = e.shiftKey;

  dragActive = true;
  dragLastKey = `${date}|${col}|${lane}`;
  dragLockedLane = null;

  const candidate = candidateFromCell(s.config, date, col, 0);
  const fits = candidate.endMinutes <= s.config.dayEndMinutes;
  const conflictAtLane0 = findConflict(s.appointments, candidate);

  if (!fits) {
    flashInvalid(date, col, lane);
    dragActive = false;
    return;
  }

  if (wantOverlap && (covering || conflictAtLane0)) {
    dragMode = 'overlap-add';
    const newLane = nextFreeLane(
      s.appointments,
      date,
      candidate.startMinutes,
      candidate.endMinutes,
    );
    dragLockedLane = newLane;
    candidate.lane = newLane;
    // Clear phantoms — the click "consumes" the phantom by making it real.
    phantomLanes = new Map();
    store.addAppointment(candidate);
    return;
  }

  if (covering) {
    dragMode = 'remove';
    store.removeAppointment(covering.id);
    return;
  }

  if (conflictAtLane0) {
    flashInvalid(date, col, lane);
    dragActive = false;
    return;
  }

  dragMode = 'add';
  store.addAppointment(candidate);
}

function applyDragAction(store: Store, date: string, col: number, lane: number): void {
  const s = store.get();
  const cellStart = slotStartMinutes(s.config, col);
  const cellEnd = cellStart + s.config.slotSizeMinutes;

  if (dragMode === 'remove') {
    const covering = appointmentAtCell(s.appointments, date, cellStart, cellEnd, lane);
    if (covering) store.removeAppointment(covering.id);
    return;
  }

  if (dragMode === 'add') {
    const candidate = candidateFromCell(s.config, date, col, 0);
    if (candidate.endMinutes > s.config.dayEndMinutes) return;
    if (findConflict(s.appointments, candidate)) return;
    store.addAppointment(candidate);
    return;
  }

  if (dragLockedLane === null) return;
  const candidate = candidateFromCell(s.config, date, col, dragLockedLane);
  if (candidate.endMinutes > s.config.dayEndMinutes) return;
  if (findConflict(s.appointments, candidate)) return;
  store.addAppointment(candidate);
}

function flashInvalid(date: string, col: number, lane: number): void {
  const cell = document.querySelector<HTMLElement>(
    `.matrix-cell[data-date="${date}"][data-col="${col}"][data-lane="${lane}"]`,
  );
  if (!cell) return;
  cell.classList.add('is-invalid');
  setTimeout(() => cell.classList.remove('is-invalid'), 400);
}

// --- Hover preview + tooltip ---

let tooltipEl: HTMLElement | null = null;
let previewEl: HTMLElement | null = null;

function onCellEnter(
  e: PointerEvent,
  date: string,
  col: number,
  lane: number,
): void {
  lastPointerEvent = e;
  // Plain time tooltip on entry. renderPreview may upgrade it to the
  // shift-hint variant (yellow "+ Shift" pill above) when the candidate
  // would conflict.
  showTooltip(e, date, col, false);
  if (dragActive) {
    hidePreview();
    return;
  }
  lastPointerCell = { date, col, lane };
  renderPreview(lastPointerCell);
}

/**
 * Top-level preview entrypoint. Decides what should be shown, sets up phantom
 * lanes if needed (which may rebuild the grid), and then places the preview
 * overlay using the (possibly fresh) DOM.
 */
function renderPreview(target: { date: string; col: number; lane: number }): void {
  if (!storeRef || !scrollerRef) return;
  const s = storeRef.get();
  const config = s.config;
  const dates = enumerateDates(config.rangeStart, config.rangeEnd, config.allowedWeekdays);
  const dateIdx = dates.indexOf(target.date);
  if (dateIdx < 0) return;

  const startMinutes = slotStartMinutes(config, target.col);
  const endMinutes = startMinutes + config.defaultDurationMinutes;
  const cellEnd = startMinutes + config.slotSizeMinutes;

  const covering = appointmentAtCell(s.appointments, target.date, startMinutes, cellEnd, target.lane);
  const candidateLane0: Appointment = {
    id: '__preview',
    date: target.date,
    lane: 0,
    startMinutes,
    endMinutes,
  };
  const conflictAtLane0 = findConflict(s.appointments, candidateLane0);
  const fits = endMinutes <= config.dayEndMinutes;

  // Decide what the preview should show.
  let previewLane = 0;
  let valid = true;
  let isOverlap = false;
  let shiftHint = false;  // show "⇧ Shift" callout when conflict is shift-fixable

  if (!fits) {
    previewLane = covering ? target.lane : 0;
    valid = false;
  } else if (shiftHeld && (covering || conflictAtLane0)) {
    previewLane = nextFreeLane(s.appointments, target.date, startMinutes, endMinutes);
    valid = true;
    isOverlap = true;
  } else if (covering || conflictAtLane0) {
    // Any "Gleichzeitigkeit" with an existing appointment — render the red
    // preview at the cursor's own lane so feedback always sits where the
    // pointer is, regardless of which lane the conflict actually resides in.
    previewLane = target.lane;
    valid = false;
    shiftHint = true;
  } else {
    previewLane = 0;
    valid = true;
  }

  // Phantom-lane management. We only ever need a phantom for the date we're
  // currently previewing, and only when the previewLane exceeds what exists.
  const realLaneCount = laneCountForDate(s.appointments, target.date);
  const newPhantoms = new Map<string, number>();
  if (isOverlap && previewLane >= realLaneCount) {
    newPhantoms.set(target.date, previewLane + 1);
  }
  if (!phantomMapsEqual(phantomLanes, newPhantoms)) {
    setPhantomLanes(newPhantoms);
    return; // setPhantomLanes already calls renderPreview again with the same target.
  }

  // Compute the absolute grid row from the (possibly phantom-extended) lane counts.
  const laneCounts = dates.map((d) => {
    const real = laneCountForDate(s.appointments, d);
    const phantom = phantomLanes.get(d) ?? 0;
    return Math.max(real, phantom);
  });
  let dayStartRow = 2;
  for (let i = 0; i < dateIdx; i++) dayStartRow += laneCounts[i];

  const remainingCols = slotCount(config) - target.col;
  const wantedSpan = config.defaultDurationMinutes / config.slotSizeMinutes;
  const spanCols = Math.max(1, Math.min(wantedSpan, remainingCols));

  const currentGrid = scrollerRef.querySelector('.matrix-grid') as HTMLElement | null;
  if (!currentGrid) return;

  if (!previewEl || previewEl.parentElement !== currentGrid) {
    previewEl = el('div', { class: 'appointment-preview' });
    currentGrid.append(previewEl);
  }

  previewEl.style.gridRow = String(dayStartRow + previewLane);
  previewEl.style.gridColumn = `${target.col + 2} / ${target.col + 2 + Math.round(spanCols)}`;
  previewEl.classList.toggle('is-invalid', !valid);
  // Preview is always teal regardless of overlap-vs-plain — the colour signals
  // "imminent placement" rather than which lane it'll land in.
  previewEl.classList.remove('is-overlap');
  void isOverlap;

  previewEl.replaceChildren(
    el('span', { class: 'appointment-preview-label' },
      `${formatTime(startMinutes)}–${formatTime(endMinutes)}`),
  );

  // Yellow "+ Shift" pill rides above the cursor's time tooltip when the
  // conflict can be resolved by holding Shift.
  if (lastPointerEvent) {
    showTooltip(lastPointerEvent, target.date, target.col, shiftHint);
  }
}

function hidePreview(): void {
  if (previewEl && previewEl.parentElement) {
    previewEl.parentElement.removeChild(previewEl);
  }
}

function getTooltip(): HTMLElement {
  if (tooltipEl) return tooltipEl;
  const t = el('div', { class: 'matrix-tooltip', role: 'tooltip' });
  t.style.display = 'none';
  document.body.append(t);
  tooltipEl = t;
  return t;
}

function showTooltip(e: PointerEvent, date: string, col: number, shiftHint = false): void {
  if (!storeRef) return;
  const t = getTooltip();
  const config = storeRef.get().config;
  const start = slotStartMinutes(config, col);
  const end = start + config.defaultDurationMinutes;
  const timeLine = `${formatDateShort(date)} · ${formatTime(start)}–${formatTime(end)}`;
  t.classList.toggle('with-shift-hint', shiftHint);
  if (shiftHint) {
    // Two stacked rows: yellow "+ Shift" pill on top, black date/time below.
    t.replaceChildren(
      el('span', { class: 'matrix-tooltip-shift' },
        '+ ',
        el('kbd', { class: 'matrix-tooltip-kbd' }, 'Shift'),
      ),
      el('span', { class: 'matrix-tooltip-time' }, timeLine),
    );
  } else {
    t.textContent = timeLine;
  }
  t.style.display = 'block';
  positionTooltip(t, e);
}

function hideTooltip(): void {
  if (tooltipEl) tooltipEl.style.display = 'none';
}

function positionTooltip(t: HTMLElement, e: PointerEvent): void {
  const offset = 12;
  t.style.left = `${e.clientX + offset}px`;
  t.style.top = `${e.clientY + offset}px`;
}
